/**
 * Live Flight Booking Monitoring — Ops Control Center (M1, read-only)
 * ====================================================================
 *
 * One-page admin module that surfaces the full booking lifecycle:
 *   • 12 dashboard cards at the top (auto-refresh every 15 s)
 *   • Filterable real-time table of all bookings
 *   • Slide-in detail drawer with summary + timeline + supplier logs +
 *     payment logs + failure diagnosis
 *
 * Theme matches the rest of the admin panel:
 *   primary = #1B375B   accent = #335E77   teal = #659A9B
 *   green = emerald   orange = amber   red = rose   blue = sky
 *
 * Backend: /api/admin/monitor/{stats,bookings,bookings/:id}
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, Search,
  X, ChevronRight, Plane, CreditCard, Receipt, AlertCircle, Filter,
  Users, IndianRupee, TrendingUp, ShieldAlert, Hourglass, FileWarning,
  Zap, UserCheck, FileText, Ticket, Ban, MessageSquare, Loader2, Repeat,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/monitor`;

const POLL_MS = 15_000;

// ─── Status tones ──────────────────────────────────────────────────
const STATUS_TONE = {
  SEARCHED:                   { bg: 'bg-gray-100',    text: 'text-gray-700',    label: 'Searched' },
  REVIEWED:                   { bg: 'bg-gray-100',    text: 'text-gray-700',    label: 'Reviewed' },
  PAYMENT_INITIATED:          { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Pay-Initiated' },
  PAYMENT_SUCCESS:            { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Paid' },
  PAYMENT_FAILED:             { bg: 'bg-rose-100',    text: 'text-rose-800',    label: 'Pay-Failed' },
  SUPPLIER_BOOKING_INITIATED: { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Supplier-Init' },
  SUPPLIER_PENDING:           { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Supplier-Pending' },
  SUPPLIER_CONFIRMED:         { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Supplier-OK' },
  PNR_GENERATED:              { bg: 'bg-sky-100',     text: 'text-sky-800',     label: 'PNR-Issued' },
  TICKET_PENDING:             { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Ticket-Pending' },
  TICKET_ISSUED:              { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Ticketed' },
  BOOKING_FAILED:             { bg: 'bg-rose-100',    text: 'text-rose-800',    label: 'Failed' },
  BOOKING_CANCELLED:          { bg: 'bg-gray-200',    text: 'text-gray-700',    label: 'Cancelled' },
  REFUND_PENDING:             { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Refund-Pending' },
  REFUNDED:                   { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Refunded' },
  MANUAL_REVIEW_REQUIRED:     { bg: 'bg-sky-100',     text: 'text-sky-800',     label: 'Manual-Review' },
};

import { fmtIST, fmtISTShort, fmtISTClock } from '../../utils/datetimeIST';

const _money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const _date = fmtISTShort;
const _shortDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }) : '—';

function StatusBadge({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.SEARCHED;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${t.bg} ${t.text}`}
      data-testid={`monitor-status-badge-${status}`}
    >
      {t.label}
    </span>
  );
}

// ─── M4 — SLA chip ──────────────────────────────────────────────────
// Renders a small live countdown / overdue chip. Pure presentational —
// the backend stamps `sla_due_at`/`sla_breached`/`sla_seconds_remaining`.
function SlaChip({ row, compact = false }) {
  if (!row || row.sla_due_at == null) return null;
  const breached = !!row.sla_breached;
  const sec = Number(row.sla_seconds_remaining || 0);
  let text;
  if (breached) {
    const overdueMin = Math.max(1, Math.floor(-sec / 60));
    text = `Overdue ${overdueMin}m`;
  } else if (sec < 60) {
    text = `${Math.max(0, sec)}s left`;
  } else if (sec < 3600) {
    text = `${Math.floor(sec / 60)}m left`;
  } else {
    text = `${Math.floor(sec / 3600)}h left`;
  }
  const tone = breached
    ? 'bg-rose-100 text-rose-900 border-rose-300'
    : sec < 60
      ? 'bg-amber-100 text-amber-900 border-amber-200'
      : 'bg-emerald-50 text-emerald-800 border-emerald-200';
  return (
    <span
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0' : 'px-2 py-0.5'} rounded-full border text-[10px] font-bold ${tone}`}
      title={row.sla_label || 'SLA'}
      data-testid={`monitor-sla-chip-${row.booking_id}`}
    >
      <Clock className="w-3 h-3" />
      {text}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, tone = 'default', sub, testId }) {
  const tones = {
    default: 'bg-white border-gray-200 text-[#1B375B]',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warn:    'bg-amber-50  border-amber-200  text-amber-900',
    danger:  'bg-rose-50   border-rose-200   text-rose-900',
    info:    'bg-sky-50    border-sky-200    text-sky-900',
  };
  return (
    <div
      className={`rounded-xl border ${tones[tone]} p-4 transition-shadow hover:shadow-sm`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <p className="text-2xl font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>{value ?? '—'}</p>
      {sub ? <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p> : null}
    </div>
  );
}

// ─── Agent typeahead (P1) ─────────────────────────────────────────────
function AgentTypeahead({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState('');
  const debRef = useRef(null);
  const boxRef = useRef(null);

  // Resolve a label for the currently-selected agent_id (best-effort).
  useEffect(() => {
    let active = true;
    if (!value) { setLabel(''); return undefined; }
    axios
      .get(`${API}/agents`, { withCredentials: true, params: { q: '', limit: 50 } })
      .then((r) => {
        if (!active) return;
        const found = (r.data?.rows || []).find((a) => a.id === value);
        if (found) setLabel(found.agency_name || found.full_name || value);
        else setLabel(value);
      })
      .catch(() => active && setLabel(value));
    return () => { active = false; };
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/agents`, {
          withCredentials: true,
          params: { q, limit: 15 },
        });
        setOpts(r.data?.rows || []);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, open]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="monitor-filter-agent-trigger"
        className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-left min-w-[180px] flex items-center justify-between gap-2 hover:border-[#1B375B]"
      >
        <span className={value ? 'text-[#1B375B] font-semibold truncate' : 'text-gray-500'}>
          {value ? (label || 'Selected agent') : 'Any agent'}
        </span>
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-gray-400" />
            <input
              autoFocus
              type="text"
              placeholder="Search agency / name / email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="monitor-filter-agent-search"
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded"
            />
          </div>
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQ(''); }}
              data-testid="monitor-filter-agent-clear"
              className="w-full text-left text-xs px-2 py-1.5 text-rose-700 hover:bg-rose-50 rounded mb-1"
            >
              Clear selection
            </button>
          )}
          <div className="max-h-[260px] overflow-y-auto" data-testid="monitor-filter-agent-list">
            {loading && <p className="text-[11px] text-gray-500 px-2 py-1.5">Loading…</p>}
            {!loading && opts.length === 0 && (
              <p className="text-[11px] text-gray-500 px-2 py-1.5">No agents found.</p>
            )}
            {!loading && opts.map((a) => (
              <button
                type="button"
                key={a.id}
                onClick={() => { onChange(a.id); setLabel(a.agency_name || a.full_name); setOpen(false); }}
                data-testid={`monitor-filter-agent-opt-${a.id}`}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 rounded"
              >
                <p className="font-semibold text-[#1B375B] truncate">{a.agency_name || a.full_name}</p>
                <p className="text-[10px] text-gray-500 truncate">{a.full_name} · {a.email}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Filters bar ──────────────────────────────────────────────────────
function FiltersBar({ filters, onChange, onReset, loading }) {
  const set = (k, v) => onChange({ ...filters, [k]: v, page: 1 });
  const [advancedOpen, setAdvancedOpen] = useState(() => Boolean(
    filters.user_type || filters.agent_id || filters.amount_min || filters.amount_max ||
    filters.has_failure_category || filters.is_lcc || filters.has_pnr ||
    filters.has_tickets,
  ));
  const advCount = [
    filters.user_type, filters.agent_id, filters.amount_min, filters.amount_max,
    filters.has_failure_category, filters.is_lcc, filters.has_pnr, filters.has_tickets,
  ].filter((v) => v !== undefined && v !== '' && v !== null).length;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-3 mb-4"
      data-testid="monitor-filters"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Booking ID / PNR / Phone / Email / Route"
            value={filters.q || ''}
            onChange={(e) => set('q', e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#1B375B]"
            data-testid="monitor-filter-search"
          />
        </div>
        <select
          value={filters.main_status || ''}
          onChange={(e) => set('main_status', e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
          data-testid="monitor-filter-status"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_TONE).map((s) => (
            <option key={s} value={s}>{STATUS_TONE[s].label}</option>
          ))}
        </select>
        <select
          value={filters.supplier || ''}
          onChange={(e) => set('supplier', e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          data-testid="monitor-filter-supplier"
        >
          <option value="">All sources</option>
          <option value="tripjack">Tripjack</option>
          <option value="riya">Riya</option>
          <option value="offline">Offline</option>
        </select>
        <input
          type="date"
          value={filters.from_date || ''}
          onChange={(e) => set('from_date', e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          data-testid="monitor-filter-from"
          title="From date"
        />
        <input
          type="date"
          value={filters.to_date || ''}
          onChange={(e) => set('to_date', e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          data-testid="monitor-filter-to"
          title="To date"
        />
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          data-testid="monitor-filter-advanced-toggle"
          className={`px-3 py-2 text-xs font-semibold rounded-lg border inline-flex items-center gap-1.5 transition-all ${
            advancedOpen || advCount > 0
              ? 'bg-[#1B375B] text-white border-[#1B375B]'
              : 'bg-white text-[#1B375B] border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Advanced
          {advCount > 0 && (
            <span
              data-testid="monitor-filter-advanced-count"
              className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-[#1B375B] text-[10px] font-bold"
            >
              {advCount}
            </span>
          )}
        </button>
      </div>

      {advancedOpen && (
        <div
          className="mt-3 pt-3 border-t border-dashed border-gray-200 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3"
          data-testid="monitor-filters-advanced"
        >
          {/* User type */}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">User type</label>
            <select
              value={filters.user_type || ''}
              onChange={(e) => set('user_type', e.target.value)}
              data-testid="monitor-filter-user-type"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">B2C + Agent</option>
              <option value="b2c">B2C only</option>
              <option value="agent">Agent only</option>
            </select>
          </div>

          {/* Agent typeahead */}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">Agent</label>
            <AgentTypeahead
              value={filters.agent_id || ''}
              onChange={(v) => set('agent_id', v)}
            />
          </div>

          {/* Amount range */}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">Amount range (₹)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                placeholder="Min"
                value={filters.amount_min || ''}
                onChange={(e) => set('amount_min', e.target.value)}
                data-testid="monitor-filter-amount-min"
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="number"
                min="0"
                placeholder="Max"
                value={filters.amount_max || ''}
                onChange={(e) => set('amount_max', e.target.value)}
                data-testid="monitor-filter-amount-max"
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
              />
            </div>
          </div>

          {/* Tri-state booleans */}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">Has failure category</label>
            <select
              value={filters.has_failure_category === undefined || filters.has_failure_category === '' ? '' : (filters.has_failure_category ? 'true' : 'false')}
              onChange={(e) => set('has_failure_category', e.target.value === '' ? '' : e.target.value === 'true')}
              data-testid="monitor-filter-has-failure"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">Any</option>
              <option value="true">Yes (has failure)</option>
              <option value="false">No</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">LCC carrier</label>
            <select
              value={filters.is_lcc === undefined || filters.is_lcc === '' ? '' : (filters.is_lcc ? 'true' : 'false')}
              onChange={(e) => set('is_lcc', e.target.value === '' ? '' : e.target.value === 'true')}
              data-testid="monitor-filter-is-lcc"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">Any</option>
              <option value="true">LCC only</option>
              <option value="false">FSC only</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">Has PNR</label>
            <select
              value={filters.has_pnr === undefined || filters.has_pnr === '' ? '' : (filters.has_pnr ? 'true' : 'false')}
              onChange={(e) => set('has_pnr', e.target.value === '' ? '' : e.target.value === 'true')}
              data-testid="monitor-filter-has-pnr"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">Any</option>
              <option value="true">PNR present</option>
              <option value="false">No PNR</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide block mb-1">Has tickets</label>
            <select
              value={filters.has_tickets === undefined || filters.has_tickets === '' ? '' : (filters.has_tickets ? 'true' : 'false')}
              onChange={(e) => set('has_tickets', e.target.value === '' ? '' : e.target.value === 'true')}
              data-testid="monitor-filter-has-tickets"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">Any</option>
              <option value="true">Tickets issued</option>
              <option value="false">No tickets</option>
            </select>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        {[
          ['failed_only', 'Failed', 'bg-rose-50 text-rose-800 border-rose-200'],
          ['pending_only', 'Pending', 'bg-amber-50 text-amber-800 border-amber-200'],
          ['manual_only', 'Manual review', 'bg-sky-50 text-sky-800 border-sky-200'],
          ['sla_breached_only', 'SLA breached', 'bg-rose-100 text-rose-900 border-rose-300'],
        ].map(([key, label, cls]) => {
          const on = !!filters[key];
          return (
            <button
              key={key}
              onClick={() => set(key, !on)}
              data-testid={`monitor-quick-${key}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                on ? `${cls} ring-2 ring-offset-1` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          );
        })}
        <button
          onClick={onReset}
          data-testid="monitor-filter-reset"
          className="px-3 py-1 text-xs font-semibold text-gray-600 hover:text-[#1B375B]"
        >
          Reset
        </button>
        {loading && (
          <span className="text-xs text-gray-500 ml-auto inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" /> Updating…
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Detail drawer (right slide-in) ────────────────────────────────────
function DetailDrawer({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('actions');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/bookings/${bookingId}`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bookingId]);

  if (!bookingId) return null;

  return (
    <div className="fixed inset-0 z-40" data-testid="monitor-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full md:w-[720px] bg-gray-50 shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Booking</p>
            <h2 className="text-lg font-bold text-[#1B375B] font-mono" data-testid="monitor-detail-bid">{bookingId}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="monitor-detail-close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </header>
        {loading && (
          <div className="p-10 text-center text-gray-500">
            <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
            Loading details…
          </div>
        )}
        {err && (
          <div className="m-5 p-4 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800">
            {String(err)}
          </div>
        )}
        {data && (
          <>
            {/* Summary */}
            <section className="bg-white border-b border-gray-200 px-5 py-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <StatusBadge status={data.monitor.main_status} />
                {data.summary?.sla_due_at != null && (
                  <SlaChip row={{
                    booking_id: bookingId,
                    sla_due_at: data.summary.sla_due_at,
                    sla_breached: data.summary.sla_breached,
                    sla_seconds_remaining: data.summary.sla_seconds_remaining,
                    sla_label: data.summary.sla_label,
                  }} />
                )}
                {data.failure_diagnosis.category && (
                  <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
                    {data.failure_diagnosis.category}
                  </span>
                )}
                {data.monitor.recovered_from_orphan && (
                  <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200">
                    Recovered
                  </span>
                )}
                {/* Supplier-authoritative status pill — shows what the supplier
                    itself thinks the booking is in. (Feb-2026 P1 enhancement.) */}
                {(data.summary?.tripjack_status || data.summary?.supplier_canonical_status) && (
                  <span
                    className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-900 border border-indigo-200 inline-flex items-center gap-1"
                    data-testid="supplier-status-pill"
                    title={`Tripjack canonical: ${data.summary.supplier_canonical_status || '—'}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                    {(data.summary.source || 'tripjack').toUpperCase()} · {data.summary.tripjack_status || data.summary.supplier_canonical_status}
                  </span>
                )}
                {data.summary?.is_lcc && (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200" data-testid="lcc-pill">
                    LCC · PNR ≡ ticket
                  </span>
                )}
                {/* User-type pill — agent vs B2C at a glance. */}
                <span
                  className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${data.summary?.user_type === 'agent' ? 'bg-emerald-50 text-emerald-900 border-emerald-200' : 'bg-blue-50 text-blue-900 border-blue-200'}`}
                  data-testid="usertype-pill"
                >
                  {data.summary?.user_type === 'agent' ? 'AGENT' : 'B2C'}
                </span>
              </div>
              {/* Agent / B2C profile strip — minimises ops cross-system lookups. */}
              {(data.summary?.agent_profile || data.summary?.b2c_profile) && (
                <div className="mb-3 p-2 rounded-lg border border-gray-100 bg-gradient-to-r from-gray-50 to-white text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="profile-strip">
                  {data.summary?.agent_profile ? (
                    <>
                      <span className="font-bold text-emerald-900 inline-flex items-center gap-1">
                        🏢 {data.summary.agent_profile.agency_name || '—'}
                      </span>
                      <span className="text-gray-700">{data.summary.agent_profile.full_name}</span>
                      <span className="text-gray-500 font-mono">{data.summary.agent_profile.email}</span>
                      {data.summary.agent_profile.phone && <span className="text-gray-500 font-mono">{data.summary.agent_profile.phone}</span>}
                      {data.summary.agent_profile.wallet_balance !== undefined && (
                        <span className="ml-auto text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          Wallet: {_money(data.summary.agent_profile.wallet_balance)}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="font-bold text-blue-900">👤 {data.summary.b2c_profile.full_name || '—'}</span>
                      <span className="text-gray-500 font-mono">{data.summary.b2c_profile.email}</span>
                      {data.summary.b2c_profile.phone && <span className="text-gray-500 font-mono">{data.summary.b2c_profile.phone}</span>}
                      {data.summary.b2c_profile.total_bookings !== undefined && (
                        <span className="ml-auto text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          {data.summary.b2c_profile.total_bookings} bookings · {_money(data.summary.b2c_profile.total_spent || 0)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Supplier · ID</p>
                  <p className="font-mono text-xs">{data.summary.supplier} · {data.summary.supplier_booking_id || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">PNR</p>
                  <p className="font-mono font-bold">{data.summary.pnr || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Route</p>
                  <p className="font-semibold">{data.summary.route || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Travel</p>
                  <p>{_shortDate(data.summary.travel_date)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Airline</p>
                  <p>{data.summary.airline || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Pax · Total</p>
                  <p className="font-semibold">{(data.summary.passengers || []).length || 1} · {_money(data.summary.total_amount)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] text-gray-500 uppercase">Contact</p>
                  <p className="text-xs">{data.summary.contact_phone || '—'} · {data.summary.contact_email || '—'}</p>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                <span className="font-semibold">Next:</span> {data.monitor.next_required_action || '—'}
              </p>
            </section>

            {/* Tabs */}
            <nav className="flex border-b border-gray-200 bg-white sticky top-[57px] z-10">
              {[
                ['actions', 'Actions', null],
                ['timeline', 'Timeline', data.timeline.length],
                ['diagnosis', 'Diagnosis', data.failure_diagnosis.category ? '!' : 0],
                ['supplier', 'Supplier', data.supplier_logs.length],
                ['payment', 'Payment', data.payment_logs.length],
              ].map(([k, label, badge]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`monitor-detail-tab-${k}`}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                    tab === k ? 'border-[#1B375B] text-[#1B375B]' : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {label}{badge ? <span className="ml-1 text-[10px] text-gray-500">({badge})</span> : null}
                </button>
              ))}
            </nav>

            {/* Tab body */}
            <div className="p-5">
              {tab === 'actions' && (
                <ActionsPanel
                  bookingId={bookingId}
                  data={data}
                  onActionDone={() => {
                    // Force re-fetch by toggling the bookingId-keyed effect
                    setLoading(true);
                    axios.get(`${API}/bookings/${bookingId}`, { withCredentials: true })
                      .then((r) => setData(r.data))
                      .finally(() => setLoading(false));
                  }}
                />
              )}
              {tab === 'timeline' && <TimelinePanel events={data.timeline} />}
              {tab === 'diagnosis' && <DiagnosisPanel fd={data.failure_diagnosis} monitor={data.monitor} />}
              {tab === 'supplier' && <LogList rows={data.supplier_logs} empty="No supplier API logs yet." testIdPrefix="sup" />}
              {tab === 'payment' && <LogList rows={data.payment_logs} empty="No payment events yet." testIdPrefix="pay" />}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function TimelinePanel({ events }) {
  if (!events.length) return <p className="text-sm text-gray-500">No timeline events.</p>;
  return (
    <ol className="relative border-l-2 border-gray-200 ml-2" data-testid="monitor-timeline">
      {events.map((e, i) => {
        const tone = STATUS_TONE[e.status] || STATUS_TONE.SEARCHED;
        return (
          <li key={i} className="ml-4 pb-4 last:pb-0">
            <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${tone.bg.replace('bg-', 'bg-').replace('100', '500')}`} style={{
              backgroundColor: e.status === 'BOOKING_FAILED' ? '#e11d48' :
                               e.status === 'TICKET_ISSUED' || e.status === 'REFUNDED' ? '#059669' :
                               e.status?.includes('PENDING') ? '#f59e0b' : '#1B375B',
            }} />
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2 mb-0.5 flex-wrap">
                <StatusBadge status={e.status} />
                <span className="text-[10px] text-gray-500">{_date(e.timestamp)}</span>
              </div>
              <p className="text-sm text-gray-800">{e.message}</p>
              <p className="text-[10px] text-gray-500 mt-1">{e.actor}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function DiagnosisPanel({ fd, monitor }) {
  if (!fd.category) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <CheckCircle className="w-5 h-5 mb-1" />
        No failure detected — booking is on its happy path.
      </div>
    );
  }
  const severityTone = fd.severity === 'CRITICAL' ? 'bg-rose-100 border-rose-300 text-rose-900' :
                       fd.severity === 'HIGH' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                       fd.severity === 'MEDIUM' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                       'bg-gray-50 border-gray-200 text-gray-800';
  return (
    <div className="space-y-3" data-testid="monitor-diagnosis">
      <div className={`rounded-lg border p-4 ${severityTone}`}>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <ShieldAlert className="w-5 h-5" />
          <span className="font-bold text-sm">{fd.category}</span>
          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-white/70">
            {fd.severity}
          </span>
          {fd.retryable && (
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
              Retryable
            </span>
          )}
          {fd.auto_action_allowed && (
            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" title="Auto-retry engine handles this">
              Auto-action OK
            </span>
          )}
          {fd.source_code && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/70 ml-auto" title={`Matched via ${fd.source_signal}`}>
              TJ-{fd.source_code}
            </span>
          )}
        </div>
        <p className="text-sm">{fd.failure_reason || '—'}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-[10px] uppercase text-gray-500">Recommended action</p>
          <p className="font-semibold mt-0.5">{fd.recommended_action || '—'}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-[10px] uppercase text-gray-500">Last successful step</p>
          <p className="font-semibold mt-0.5">{monitor.last_successful_step}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-[10px] uppercase text-gray-500">Payment captured</p>
          <p className="font-semibold mt-0.5">{fd.payment_captured ? 'Yes' : 'No'}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-[10px] uppercase text-gray-500">Customer impacted</p>
          <p className="font-semibold mt-0.5">{fd.customer_impacted ? 'Yes' : 'No'}</p>
        </div>
        {fd.supplier_error_code && (
          <div className="rounded-lg border border-gray-200 bg-white p-3 col-span-2">
            <p className="text-[10px] uppercase text-gray-500">Supplier error</p>
            <p className="font-mono text-xs mt-0.5">{fd.supplier_error_code} · {fd.supplier_error_message}</p>
          </div>
        )}
        {fd.source_signal && (
          <div className="rounded-lg border border-gray-200 bg-white p-3 col-span-2">
            <p className="text-[10px] uppercase text-gray-500">Classification source</p>
            <p className="font-mono text-xs mt-0.5">
              Matched via <span className="font-bold">{fd.source_signal}</span>
              {fd.source_code ? <span> · Tripjack code <span className="font-bold">{fd.source_code}</span></span> : null}
            </p>
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-500 italic">
        Use the <span className="font-bold">Actions</span> tab to apply manual resolutions — all audit-logged.
      </p>
    </div>
  );
}

function LogList({ rows, empty, testIdPrefix }) {
  if (!rows.length) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <ul className="space-y-2" data-testid={`monitor-loglist-${testIdPrefix}`}>
      {rows.map((r, i) => {
        const isErr = r.status >= 400 || r.error_code;
        const isSlow = (r.latency_ms || 0) > 3000;
        return (
        <li key={i} className={`rounded-lg border p-3 text-xs ${isErr ? 'border-rose-200 bg-rose-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <span className="font-semibold uppercase text-[10px] text-gray-500">{r.kind || r.type || 'event'}</span>
            <span className="text-[10px] text-gray-500">{_date(r.created_at)}</span>
          </div>
          {r.endpoint && <p className="font-mono text-[11px] text-gray-700 break-all">{r.endpoint}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {r.status != null && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                Number(r.status) >= 400 ? 'bg-rose-100 text-rose-800' :
                Number(r.status) >= 200 && Number(r.status) < 300 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
              }`}>
                HTTP {r.status}
              </span>
            )}
            {r.latency_ms != null && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isSlow ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-700'}`}>
                {r.latency_ms}ms
              </span>
            )}
            {r.supplier && <span className="text-[10px] text-gray-600">· {r.supplier}</span>}
          </div>
          {r.amount != null && <p className="mt-1"><span className="text-gray-500">Amount:</span> <span className="font-bold">{_money(r.amount)}</span></p>}
          {r.error_code && <p className="text-rose-800 mt-1 font-mono text-[10px]">err={r.error_code} · {r.error_message}</p>}
          {r.reason && <p className="text-rose-800 mt-1">{r.reason}</p>}
          {r.ref && <p className="font-mono text-[10px] text-gray-500 mt-1 break-all">{r.ref}</p>}
        </li>
        );
      })}
    </ul>
  );
}

// ─── M3 — Actions panel (10 manual resolution endpoints) ──────────────
function ActionsPanel({ bookingId, data, onActionDone }) {
  const [busy, setBusy] = useState(null); // action key while in-flight
  const [toast, setToast] = useState(null);
  const [confirming, setConfirming] = useState(null); // {action, body}
  const [assignOpen, setAssignOpen] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [assignForm, setAssignForm] = useState({ assigned_to: '', note: '' });
  // M7: Smart Refund modal state
  const [refundTicketOpen, setRefundTicketOpen] = useState(false);
  const [refundTicketNote, setRefundTicketNote] = useState('');
  // M8 Tier 2 — Replay modal state
  const [replayOpen, setReplayOpen] = useState(false);
  const [replayElig, setReplayElig] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayConfirm, setReplayConfirm] = useState('');
  const [replayPreCheck, setReplayPreCheck] = useState(true);
  const [replayNote, setReplayNote] = useState('');

  const fd = data?.failure_diagnosis || {};
  const mon = data?.monitor || {};

  const call = async (action, body = {}) => {
    setBusy(action);
    setToast(null);
    try {
      const r = await axios.post(
        `${API}/bookings/${bookingId}/${action}`,
        body,
        { withCredentials: true },
      );
      const m = r.data?.message || `${action} done`;
      setToast({ kind: 'ok', text: m });
      if (onActionDone) onActionDone();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || `${action} failed` });
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  const prompt = (action, label, fields = []) => {
    const body = {};
    for (const f of fields) {
      // eslint-disable-next-line no-alert
      const v = window.prompt(`${label} · ${f.label}${f.optional ? ' (optional)' : ''}`, f.default || '');
      if (v === null) return; // cancelled
      if (!v && !f.optional) return;
      if (v) body[f.key] = f.type === 'number' ? Number(v) : v;
    }
    call(action, body);
  };

  // Action visibility — show only what's contextually useful.
  const actions = useMemo(() => {
    const list = [];
    // Recovery actions — only when there's a known failure
    if (fd.category === 'PAYMENT_SUCCESS_SUPPLIER_FAILED' || fd.category === 'SUPPLIER_TIMEOUT' || fd.category === 'SUPPLIER_DOWN') {
      list.push({ key: 'retry-supplier', label: 'Retry supplier booking', icon: RefreshCw, tone: 'primary' });
    }
    if (mon.main_status === 'PNR_GENERATED' || mon.main_status === 'TICKET_PENDING' || fd.category === 'TICKET_ISSUE_FAILED' || fd.category === 'PNR_NOT_RECEIVED') {
      list.push({ key: 'retry-ticketing', label: 'Refresh from supplier', icon: Ticket, tone: 'primary' });
    }
    if (mon.payment_status === 'PENDING' || mon.payment_status === 'FAILED') {
      list.push({ key: 'recheck-payment', label: 'Re-check payment status', icon: CreditCard, tone: 'default' });
    }
    if (fd.category === 'FARE_CHANGED' || fd.category === 'PRICE_MISMATCH') {
      list.push({ key: 'revalidate-fare', label: 'Revalidate fare', icon: TrendingUp, tone: 'default' });
    }
    // Always available — ops triage
    list.push({ key: 'assign', label: 'Assign to staff', icon: UserCheck, tone: 'default', requiresInput: 'assign' });
    list.push({ key: 'add-note', label: 'Add note', icon: MessageSquare, tone: 'default', requiresInput: 'note' });
    // Refund — only if paid + not already refunded
    if (fd.payment_captured && mon.main_status !== 'REFUNDED') {
      list.push({ key: 'create-refund-ticket', label: 'Open refund ticket (Support)', icon: IndianRupee, tone: 'danger', requiresInput: 'refund_ticket' });
    }
    // Manual data stamps — for stuck supplier states
    if (!data?.summary?.pnr) {
      list.push({ key: 'manual-pnr', label: 'Stamp manual PNR', icon: FileText, tone: 'default', requiresInput: 'pnr' });
    }
    if (!(data?.summary?.tickets || []).length) {
      list.push({ key: 'manual-ticket', label: 'Stamp manual ticket(s)', icon: Ticket, tone: 'default', requiresInput: 'ticket' });
    }
    // Force close — only for non-terminal states
    if (mon.main_status !== 'REFUNDED' && mon.main_status !== 'BOOKING_CANCELLED') {
      list.push({ key: 'force-close', label: 'Force close', icon: Ban, tone: 'danger', requiresInput: 'close' });
    }
    // M8 Tier 2 — Replay (eligibility-driven, FE asks /eligibility on demand)
    if (['BOOKING_FAILED','SUPPLIER_BOOKING_INITIATED','PNR_GENERATED','TICKET_PENDING','SUPPLIER_PENDING'].includes(mon.main_status)) {
      list.push({ key: 'replay', label: 'Replay booking', icon: Repeat, tone: 'danger' });
    }
    return list;
  }, [data, fd, mon]);

  const toneClasses = {
    primary: 'border-[#1B375B] bg-[#1B375B] text-white hover:bg-[#0f2742]',
    default: 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50',
    danger:  'border-rose-200  bg-rose-50  text-rose-900 hover:bg-rose-100',
  };

  const handleClick = (a) => {
    if (busy) return;
    switch (a.requiresInput) {
      case 'note':
        prompt('add-note', 'Add note', [{ key: 'note', label: 'Note' }]);
        break;
      case 'assign':
        // M6: typeahead-style dropdown from /api/admin/staff (no freetext)
        setAssignForm({ assigned_to: '', note: '' });
        setAssignOpen(true);
        if (staffOptions.length === 0) {
          setStaffLoading(true);
          axios
            .get(`${BACKEND_URL}/api/admin/staff`, {
              params: { active_only: true },
              withCredentials: true,
            })
            .then((r) => setStaffOptions(r.data?.rows || []))
            .catch(() => setStaffOptions([]))
            .finally(() => setStaffLoading(false));
        }
        break;
      case 'refund_ticket':
        setRefundTicketNote('');
        setRefundTicketOpen(true);
        break;
      case 'replay':
        // M8 Tier 2: Replay modal (eligibility-driven + confirm-by-typing)
        setReplayElig(null);
        setReplayConfirm('');
        setReplayPreCheck(true);
        setReplayNote('');
        setReplayOpen(true);
        setReplayLoading(true);
        axios
          .get(`${BACKEND_URL}/api/admin/replay/${bookingId}/eligibility`,
               { withCredentials: true })
          .then((r) => setReplayElig(r.data))
          .catch((e) => setReplayElig({
            eligible: false,
            reason: e?.response?.data?.detail || 'Eligibility check failed',
          }))
          .finally(() => setReplayLoading(false));
        break;
      case 'pnr':
        prompt('manual-pnr', 'Manual PNR', [
          { key: 'pnr', label: 'PNR' },
          { key: 'note', label: 'Note', optional: true },
        ]);
        break;
      case 'ticket':
        // eslint-disable-next-line no-alert
        { const num = window.prompt('Ticket number'); if (!num) return;
          // eslint-disable-next-line no-alert
          const pnr = window.prompt('PNR (optional)', '') || undefined;
          call('manual-ticket', { tickets: [{ ticket_no: num }], pnr });
        }
        break;
      case 'close':
        prompt('force-close', 'Force close', [{ key: 'reason', label: 'Reason' }]);
        break;
      default:
        // No-arg endpoints — confirm once before firing destructive ones.
        if (a.tone === 'danger' || a.key === 'retry-supplier' || a.key === 'retry-ticketing') {
          // eslint-disable-next-line no-alert, no-restricted-globals
          if (!confirm(`${a.label}?\n\nThis will be audit-logged.`)) return;
        }
        call(a.key);
    }
  };

  return (
    <div className="space-y-3" data-testid="monitor-actions-panel">
      {toast && (
        <div
          className={`text-xs px-3 py-2 rounded-lg border ${
            toast.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-rose-50 border-rose-200 text-rose-900'
          }`}
          data-testid="monitor-actions-toast"
        >
          {toast.text}
        </div>
      )}
      <p className="text-[11px] text-gray-500">
        Every action is audit-logged to <code className="px-1 bg-gray-100 rounded">manual_actions</code>
        {' '}and emits a timeline event. Duplicate clicks within 30 s are rejected.
      </p>
      <div className="grid grid-cols-1 gap-2">
        {actions.map((a) => {
          const Icon = a.icon || Zap;
          const isBusy = busy === a.key;
          return (
            <button
              key={a.key}
              onClick={() => handleClick(a)}
              disabled={!!busy}
              data-testid={`act-${a.key}`}
              className={`inline-flex items-center justify-between w-full px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${toneClasses[a.tone] || toneClasses.default} disabled:opacity-50`}
            >
              <span className="inline-flex items-center gap-2">
                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                {a.label}
              </span>
              <ChevronRight className="w-4 h-4 opacity-50" />
            </button>
          );
        })}
        {actions.length === 0 && (
          <p className="text-sm text-gray-500">No actions applicable for this booking right now.</p>
        )}
      </div>

      {/* M6 — Assign-to-Staff modal (replaces freetext prompt) */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="assign-staff-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAssignOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-[#1B375B] mb-1 inline-flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#659A9B]" />
              Assign to staff
            </h3>
            <p className="text-[11px] text-gray-500 mb-4">
              Pick an active ops team member. The action is audit-logged.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500">Staff member</label>
                {staffLoading ? (
                  <div className="text-xs text-gray-500 flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading active staff…
                  </div>
                ) : staffOptions.length === 0 ? (
                  <div className="text-xs text-rose-700 px-3 py-2 border border-rose-200 bg-rose-50 rounded-lg">
                    No active staff found. Add staff under Staff Management first.
                  </div>
                ) : (
                  <select
                    value={assignForm.assigned_to}
                    onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid="assign-staff-select"
                  >
                    <option value="">— Select staff —</option>
                    {staffOptions.map((s) => (
                      <option key={s.id} value={s.email}>
                        {s.name} · {s.email} ({s.role})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500">Note (optional)</label>
                <textarea
                  rows={2}
                  value={assignForm.note}
                  onChange={(e) => setAssignForm({ ...assignForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder="Why are you assigning this booking?"
                  data-testid="assign-staff-note"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setAssignOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                data-testid="assign-staff-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!assignForm.assigned_to) return;
                  const body = { assigned_to: assignForm.assigned_to };
                  if (assignForm.note) body.note = assignForm.note;
                  setAssignOpen(false);
                  call('assign', body);
                }}
                disabled={!assignForm.assigned_to || busy === 'assign'}
                className="px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg hover:bg-[#0f2742] disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="assign-staff-save"
              >
                {busy === 'assign' ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {refundTicketOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-refund-ticket-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setRefundTicketOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-[#1B375B] mb-1 inline-flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-rose-600" />
              Open refund ticket
            </h3>
            <p className="text-[11px] text-gray-500 mb-4">
              Process refunds in <strong>Support Dashboard</strong> only (auto credit note + settlement). Service fee is never refunded.
            </p>
            <div>
              <label className="text-[10px] uppercase font-semibold text-gray-500">Context note (optional)</label>
              <textarea
                rows={3}
                value={refundTicketNote}
                onChange={(e) => setRefundTicketNote(e.target.value)}
                placeholder="Why is a refund needed?"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mt-1"
                data-testid="refund-ticket-note"
              />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setRefundTicketOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                data-testid="refund-ticket-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const body = {};
                  if (refundTicketNote.trim()) body.note = refundTicketNote.trim();
                  setRefundTicketOpen(false);
                  call('create-refund-ticket', body);
                }}
                disabled={busy === 'create-refund-ticket'}
                className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded-lg hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="refund-ticket-confirm"
              >
                {busy === 'create-refund-ticket' ? <Loader2 className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />}
                Create ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* M8 Tier 2 — Replay modal */}
      {replayOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="replay-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setReplayOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#1B375B] mb-1 inline-flex items-center gap-2">
              <Repeat className="w-5 h-5 text-rose-600" />
              Replay booking
            </h3>
            <p className="text-[11px] text-gray-500 mb-3">
              Replay <strong>never re-charges</strong> the customer. The pre-check first asks the supplier whether the booking actually went through (ghost-success recovery), and only flags for manual replay if it didn&apos;t.
            </p>

            {replayLoading && (
              <div className="text-xs text-gray-500 inline-flex items-center gap-2 py-3">
                <Loader2 className="w-3 h-3 animate-spin" /> Checking eligibility…
              </div>
            )}

            {!replayLoading && replayElig && !replayElig.eligible && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-900" data-testid="replay-not-eligible">
                <p className="font-bold mb-1">Not eligible</p>
                <p>{replayElig.reason}</p>
                {replayElig.attempts_used !== undefined && (
                  <p className="mt-1 font-mono">Attempts used: {replayElig.attempts_used} / {replayElig.max_attempts}</p>
                )}
              </div>
            )}

            {!replayLoading && replayElig && replayElig.eligible && (
              <>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-3 text-xs">
                  <p className="font-bold text-amber-900 mb-1">Original failure</p>
                  <p className="text-amber-800 break-words">{replayElig.original_failure || '— (no recorded reason)'}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] text-amber-700">
                    <div><span className="font-bold">State:</span> {replayElig.main_status}</div>
                    <div><span className="font-bold">Attempts:</span> {replayElig.attempts_used} / {replayElig.max_attempts}</div>
                    <div className="col-span-2 font-mono"><span className="font-bold">Old supplier ID:</span> {replayElig.original_supplier_id || '—'}</div>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-xs mb-3 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={replayPreCheck}
                    onChange={(e) => setReplayPreCheck(e.target.checked)}
                    data-testid="replay-precheck-toggle"
                  />
                  <div>
                    <p className="font-bold">Pre-check supplier first <span className="text-emerald-700">(recommended)</span></p>
                    <p className="text-[10px] text-gray-500">Catches ghost-success: booking that already went through but our system missed the response. ~3 s.</p>
                  </div>
                </label>

                <div className="mb-3">
                  <label className="text-[10px] uppercase font-semibold text-gray-500">Note (optional)</label>
                  <textarea
                    rows={2}
                    value={replayNote}
                    onChange={(e) => setReplayNote(e.target.value)}
                    placeholder="Why is this booking being replayed?"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid="replay-note"
                  />
                </div>

                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-3">
                  <label className="text-[10px] uppercase font-bold text-rose-900">
                    Type the booking-id back to confirm
                  </label>
                  <input
                    value={replayConfirm}
                    onChange={(e) => setReplayConfirm(e.target.value)}
                    placeholder={bookingId}
                    className="mt-1 w-full px-3 py-2 border border-rose-300 rounded-lg text-sm font-mono"
                    data-testid="replay-confirm-input"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setReplayOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                data-testid="replay-cancel"
              >
                Cancel
              </button>
              {replayElig?.eligible && (
                <button
                  onClick={async () => {
                    if (replayConfirm.trim() !== bookingId.trim()) return;
                    setBusy('replay');
                    setToast(null);
                    try {
                      const r = await axios.post(
                        `${BACKEND_URL}/api/admin/replay/${bookingId}`,
                        {
                          confirm_booking_id: replayConfirm.trim(),
                          pre_check: replayPreCheck,
                          note: replayNote || undefined,
                        },
                        { withCredentials: true },
                      );
                      const out = r.data;
                      setReplayOpen(false);
                      setToast({
                        kind: out.ghost_success ? 'ok' : 'warn',
                        text: out.ghost_success
                          ? `Ghost-success recovered ✓ booking flipped to TICKET_ISSUED`
                          : `Replay #${out.attempt} recorded · manual recovery required`,
                      });
                      if (onActionDone) onActionDone();
                    } catch (e) {
                      setToast({
                        kind: 'err',
                        text: e?.response?.data?.detail || 'Replay failed',
                      });
                    } finally {
                      setBusy(null);
                    }
                  }}
                  disabled={
                    !replayElig?.eligible ||
                    replayConfirm.trim() !== bookingId.trim() ||
                    busy === 'replay'
                  }
                  className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded-lg hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="replay-confirm-btn"
                >
                  {busy === 'replay' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
                  Replay
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
// URL ↔ filter helpers: keep advanced filters reflected in the URL so
// shareable links work (e.g. "show me Tripjack failures over ₹50k for
// agency X this week").
const FILTER_URL_KEYS = [
  'q', 'main_status', 'supplier', 'from_date', 'to_date',
  'failed_only', 'pending_only', 'manual_only', 'sla_breached_only',
  'user_type', 'agent_id', 'amount_min', 'amount_max',
  'has_failure_category', 'is_lcc', 'has_pnr', 'has_tickets',
  'page', 'limit',
];
const BOOL_FILTER_KEYS = new Set([
  'failed_only', 'pending_only', 'manual_only', 'sla_breached_only',
]);
const TRISTATE_FILTER_KEYS = new Set([
  'has_failure_category', 'is_lcc', 'has_pnr', 'has_tickets',
]);

function filtersToParams(f) {
  const out = {};
  FILTER_URL_KEYS.forEach((k) => {
    const v = f[k];
    if (v === undefined || v === null || v === '') return;
    if (BOOL_FILTER_KEYS.has(k) && v) out[k] = '1';
    else if (TRISTATE_FILTER_KEYS.has(k)) out[k] = v ? '1' : '0';
    else out[k] = String(v);
  });
  return out;
}

function paramsToFilters(sp) {
  const f = { page: 1, limit: 25 };
  FILTER_URL_KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v === null) return;
    if (BOOL_FILTER_KEYS.has(k)) f[k] = v === '1' || v === 'true';
    else if (TRISTATE_FILTER_KEYS.has(k)) f[k] = v === '1' || v === 'true';
    else if (k === 'page' || k === 'limit') f[k] = Number(v) || f[k];
    else f[k] = v;
  });
  return f;
}

export default function OpsControlCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(() => paramsToFilters(searchParams));

  // Sync filters → URL whenever they change. Use replace to avoid
  // polluting browser history for every keystroke.
  useEffect(() => {
    setSearchParams(filtersToParams(filters), { replace: true });
  }, [filters, setSearchParams]);

  const queryFromFilters = useCallback(() => {
    const p = { page: filters.page, limit: filters.limit };
    if (filters.main_status) p.main_status = filters.main_status;
    if (filters.supplier) p.supplier = filters.supplier;
    if (filters.failed_only) p.failed_only = true;
    if (filters.pending_only) p.pending_only = true;
    if (filters.manual_only) p.manual_only = true;
    if (filters.sla_breached_only) p.sla_breached_only = true;
    if (filters.from_date) p.from_date = filters.from_date;
    if (filters.to_date) p.to_date = filters.to_date;
    // P1 advanced filters
    if (filters.user_type) p.user_type = filters.user_type;
    if (filters.agent_id) p.agent_id = filters.agent_id;
    if (filters.amount_min !== undefined && filters.amount_min !== '') p.amount_min = filters.amount_min;
    if (filters.amount_max !== undefined && filters.amount_max !== '') p.amount_max = filters.amount_max;
    if (filters.has_failure_category === true || filters.has_failure_category === false) {
      p.has_failure_category = filters.has_failure_category;
    }
    if (filters.is_lcc === true || filters.is_lcc === false) p.is_lcc = filters.is_lcc;
    if (filters.has_pnr === true || filters.has_pnr === false) p.has_pnr = filters.has_pnr;
    if (filters.has_tickets === true || filters.has_tickets === false) p.has_tickets = filters.has_tickets;
    // Search box → smart mapping to backend field
    if (filters.q && filters.q.trim()) {
      const q = filters.q.trim();
      if (/^[+0-9 ()-]{6,}$/.test(q))      p.phone = q;
      else if (q.includes('@'))            p.email = q;
      else if (/^[A-Z0-9]{5,8}$/i.test(q)) p.pnr = q;
      else if (/^[A-Z]{3}\s*→/i.test(q) || /^[A-Z]{3}-[A-Z]{3}/i.test(q)) p.route = q;
      else                                 p.booking_id = q;
    }
    return p;
  }, [filters]);

  const fetchAll = useCallback(async (isPoll = false) => {
    if (isPoll) setPolling(true); else setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/stats?window_hours=24`, { withCredentials: true }),
        axios.get(`${API}/bookings`, { withCredentials: true, params: queryFromFilters() }),
      ]);
      setStats(s.data);
      setRows(l.data.rows || []);
      setTotalRows(l.data.total || 0);
    } catch (e) {
      // Swallow on poll, surface on first load.
      if (!isPoll) console.error('Monitor fetch failed', e);
    } finally {
      if (isPoll) setPolling(false); else setLoading(false);
    }
  }, [queryFromFilters]);

  useEffect(() => { fetchAll(false); }, [fetchAll]);

  // Poll every POLL_MS — pause when a detail drawer is open to keep the
  // request budget lean (the drawer is the focus, the table can wait).
  useEffect(() => {
    if (selected) return undefined;
    const id = setInterval(() => fetchAll(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll, selected]);

  const cards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Total (24 h)',       value: stats.total_today,                 icon: Activity,    tone: 'default', testId: 'card-total' },
      { label: 'Successful',         value: stats.successful,                  icon: CheckCircle, tone: 'success', testId: 'card-success' },
      { label: 'Failed',             value: stats.failed,                      icon: AlertTriangle, tone: 'danger', testId: 'card-failed' },
      { label: 'Pending',            value: stats.pending,                     icon: Hourglass,   tone: 'warn',    testId: 'card-pending' },
      { label: 'Paid · Sup-Pending', value: stats.payment_paid_supplier_pending, icon: CreditCard, tone: 'warn',  testId: 'card-paid-sup-pending' },
      { label: 'Paid · No-Ticket',   value: stats.payment_paid_no_ticket,      icon: Receipt,     tone: 'warn',    testId: 'card-paid-no-ticket' },
      { label: 'Supplier Timeouts',  value: stats.supplier_timeouts,           icon: FileWarning, tone: 'danger',  testId: 'card-supplier-timeouts' },
      { label: 'Fare Changed',       value: stats.fare_changed,                icon: TrendingUp,  tone: 'info',    testId: 'card-fare-changed' },
      { label: 'Manual Review',      value: stats.manual_review_required,     icon: ShieldAlert, tone: 'info',    testId: 'card-manual-review' },
      { label: 'Refund Pending',     value: stats.refund_pending,              icon: IndianRupee, tone: 'warn',    testId: 'card-refund-pending' },
      { label: 'SLA Breached',       value: stats.sla_breached ?? 0,           icon: AlertCircle, tone: (stats.sla_breached || 0) > 0 ? 'danger' : 'default', testId: 'card-sla-breached' },
      { label: 'Supplier-OK %',      value: `${stats.supplier_success_rate_pct}%`, icon: TrendingUp, tone: stats.supplier_success_rate_pct >= 80 ? 'success' : 'warn', testId: 'card-supplier-success-rate' },
      { label: 'Last refresh',       value: _date(stats.generated_at).split(',')[1] || '—', icon: Clock, tone: 'default', testId: 'card-last-refresh' },
    ];
  }, [stats]);

  const totalPages = Math.max(1, Math.ceil(totalRows / filters.limit));

  return (
    <div className="px-4 py-5" data-testid="ops-control-center">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Activity className="w-6 h-6 text-[#659A9B]" />
            Live Booking Monitoring
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Real-time view across Tripjack + Riya + Offline · auto-refreshes every 15 s
          </p>
        </div>
        <button
          onClick={() => fetchAll(false)}
          data-testid="monitor-refresh-btn"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-[#1B375B] hover:bg-gray-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </header>

      {/* Cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-5">
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </section>

      {/* Filters */}
      <FiltersBar
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters({ page: 1, limit: 25 })}
        loading={polling}
      />

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="monitor-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Booking</th>
                <th className="text-left px-3 py-2.5 font-semibold">Created</th>
                <th className="text-left px-3 py-2.5 font-semibold">Customer</th>
                <th className="text-left px-3 py-2.5 font-semibold">Route · Date</th>
                <th className="text-left px-3 py-2.5 font-semibold">Supplier · Airline</th>
                <th className="text-right px-3 py-2.5 font-semibold">Amount</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 font-semibold">Pay · Sup · Tkt</th>
                <th className="px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                  {loading ? 'Loading…' : 'No bookings match these filters.'}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.booking_id}
                  onClick={() => setSelected(r.booking_id)}
                  className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  data-testid={`monitor-row-${r.booking_id}`}
                >
                  <td className="px-4 py-2.5">
                    <p className="font-mono text-xs font-bold text-[#1B375B]">{r.booking_id}</p>
                    {r.pnr && <p className="font-mono text-[10px] text-gray-500">PNR · {r.pnr}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">{_date(r.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs font-semibold text-gray-800 truncate max-w-[150px]">{r.customer_name}</p>
                    <p className="text-[10px] text-gray-500">{r.contact_phone || '—'}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs font-semibold">{r.route || '—'}</p>
                    <p className="text-[10px] text-gray-500">{_shortDate(r.travel_date)}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs">{r.supplier || '—'}</p>
                    <p className="text-[10px] text-gray-500 truncate max-w-[110px]">{r.airline || '—'}</p>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-bold">{_money(r.total_amount)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={r.main_status} />
                      <SlaChip row={r} compact />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[10px] font-mono">
                    <span className={r.payment_status === 'PAID' ? 'text-emerald-700' : r.payment_status === 'FAILED' ? 'text-rose-700' : 'text-amber-700'}>{r.payment_status}</span>
                    {' · '}
                    <span className={r.supplier_status === 'CONFIRMED' ? 'text-emerald-700' : r.supplier_status === 'FAILED' ? 'text-rose-700' : 'text-amber-700'}>{r.supplier_status}</span>
                    {' · '}
                    <span className={r.ticket_status === 'ISSUED' ? 'text-emerald-700' : 'text-amber-700'}>{r.ticket_status}</span>
                  </td>
                  <td className="px-2 py-2.5">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalRows > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-600 bg-gray-50">
            <span>
              Showing <span className="font-bold">{(filters.page - 1) * filters.limit + 1}</span>
              {'–'}
              <span className="font-bold">{Math.min(filters.page * filters.limit, totalRows)}</span>
              {' of '}
              <span className="font-bold">{totalRows}</span>
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={filters.page <= 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50 hover:bg-white"
                data-testid="monitor-page-prev"
              >Prev</button>
              <span className="px-2 font-semibold">{filters.page} / {totalPages}</span>
              <button
                disabled={filters.page >= totalPages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50 hover:bg-white"
                data-testid="monitor-page-next"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      <DetailDrawer bookingId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
